/**
 * Resaltados — modelo y anclaje.
 *
 * Un resaltado NO se escribe dentro del documento. El `.md` queda byte por byte
 * como estaba: cualquier otra app lo lee sin rastros de que existimos. Los
 * resaltados viven en un archivo lateral (ver vault/highlightStore.ts) y se
 * anclan al texto por posición + cita.
 *
 * Por qué no inline: no hay resaltado en el markdown estándar, así que la
 * sintaxis `==texto==` no se lee bien fuera de la app, y al marcar media
 * palabra los marcadores parten palabras y ensucian el texto de forma
 * irreversible. Escribir dentro del archivo hacía que un gesto de lectura
 * modificara el documento; ahora no lo toca.
 *
 * El precio de estar afuera es tener que reencontrar el texto cuando cambia.
 * Se paga con dos mecanismos:
 *
 *  - Editando DENTRO de la app, las posiciones se mapean con el changeset de
 *    CodeMirror: exactas siempre, sin deriva. Es el caso normal.
 *  - Editando desde AFUERA (Obsidian, un editor de texto), al abrir se busca la
 *    cita en el documento y se desempata con el contexto. Si no aparece, el
 *    resaltado queda HUÉRFANO y se muestra como tal, en vez de pintarse en el
 *    lugar equivocado. Falla a la vista, nunca en silencio.
 */

export type HighlightColor =
  | "clave"
  | "duda"
  | "def"
  | "idea"
  | "ejemplo"
  | "cita";

/**
 * Orden en el que se ofrecen en la barra, y también el de los atajos
 * `⌘⇧1`…`⌘⇧6`. Los cuatro primeros no se mueven: cambiarlos rompería el atajo
 * que ya tiene aprendido cualquiera que venga usando la app.
 */
export const HIGHLIGHT_COLORS: readonly HighlightColor[] = [
  "clave",
  "duda",
  "def",
  "idea",
  "ejemplo",
  "cita",
];

/** Nombres semánticos: filtrar por "duda" dice algo; filtrar por "amarillo" no. */
export const HIGHLIGHT_LABELS: Record<HighlightColor, string> = {
  clave: "Key",
  duda: "Question",
  def: "Definition",
  idea: "My idea",
  ejemplo: "Example",
  cita: "Quote",
};

/** Clase CSS del span resaltado. */
export function highlightClass(color: HighlightColor): string {
  return `hl hl-${color}`;
}

export function isHighlightColor(v: unknown): v is HighlightColor {
  return typeof v === "string" && (HIGHLIGHT_COLORS as readonly string[]).includes(v);
}

// ---- anclaje --------------------------------------------------------------

/** Caracteres de contexto que se guardan a cada lado para desempatar. */
export const ANCHOR_CONTEXT = 32;

/**
 * Ancla sobre texto que puede cambiar (markdown renderizado). La cita es la
 * verdad; los offsets son sólo un atajo.
 */
export interface TextAnchor {
  kind: "text";
  start: number;
  end: number;
  quote: string;
  /** Texto inmediatamente anterior y posterior, para elegir entre repeticiones. */
  prefix: string;
  suffix: string;
}

/**
 * Ancla sobre una página de PDF. Un PDF no cambia nunca, así que la posición
 * ES la verdad y no hay nada que reubicar: no existen huérfanos de este lado.
 *
 * `quote` guarda el texto que quedó debajo — vacío en un escaneo o en un
 * recorte de área. Sirve para que el panel y "convertir en tarjeta" funcionen
 * igual para PDF que para markdown, sin ramas.
 */
export interface RectAnchor {
  kind: "rect";
  /** Índice de página, base 0. */
  page: number;
  /** Rectángulos normalizados 0..1 respecto de la página: [x, y, ancho, alto]. */
  rects: Array<[number, number, number, number]>;
  quote: string;
  /**
   * Marca de ÁREA (recuadro dibujado a mano) en vez de una selección de texto.
   *
   * Se guarda explícito y no se deduce de `quote` vacío, porque una selección
   * de texto también puede quedar sin cita. Cambia sólo cómo se dibuja: un
   * relleno multiply sobre una figura la tapa, así que el área va con borde y
   * un tinte suave.
   */
  area?: boolean;
}

export type HighlightAnchor = TextAnchor | RectAnchor;

export interface Highlight {
  id: string;
  color: HighlightColor;
  anchor: HighlightAnchor;
  createdAt: number;
}

export function makeAnchor(doc: string, from: number, to: number): TextAnchor {
  return {
    kind: "text",
    start: from,
    end: to,
    quote: doc.slice(from, to),
    prefix: doc.slice(Math.max(0, from - ANCHOR_CONTEXT), from),
    suffix: doc.slice(to, Math.min(doc.length, to + ANCHOR_CONTEXT)),
  };
}

export function makeRectAnchor(
  page: number,
  rects: Array<[number, number, number, number]>,
  quote: string,
  area = false,
): RectAnchor {
  return area ? { kind: "rect", page, rects, quote, area } : { kind: "rect", page, rects, quote };
}

function commonSuffixLen(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n++;
  return n;
}

function commonPrefixLen(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n++;
  return n;
}

/** Tope de apariciones a considerar, para que una cita muy corta no dispare
 *  una búsqueda cara en un documento grande. */
const MAX_CANDIDATES = 500;

/**
 * Ubica el ancla en `doc`. Devuelve `null` cuando el texto citado ya no está:
 * ese resaltado es huérfano y hay que mostrarlo como tal.
 */
export function resolveAnchor(
  doc: string,
  a: TextAnchor,
): { from: number; to: number } | null {
  if (!a.quote) return null;

  // 1. Nada cambió alrededor: los offsets siguen siendo válidos.
  if (doc.slice(a.start, a.end) === a.quote) return { from: a.start, to: a.end };

  // 2. El texto se movió: buscarlo.
  const hits: number[] = [];
  for (let i = doc.indexOf(a.quote); i >= 0; i = doc.indexOf(a.quote, i + 1)) {
    hits.push(i);
    if (hits.length >= MAX_CANDIDATES) break;
  }
  if (hits.length === 0) return null;
  if (hits.length === 1) return { from: hits[0], to: hits[0] + a.quote.length };

  // 3. Se repite: gana la aparición cuyo entorno se parece más al guardado, y
  //    ante empate la más cercana a donde estaba.
  let best = hits[0];
  let bestScore = -1;
  let bestDist = Infinity;
  for (const i of hits) {
    const end = i + a.quote.length;
    const score =
      commonSuffixLen(doc.slice(Math.max(0, i - ANCHOR_CONTEXT), i), a.prefix) +
      commonPrefixLen(doc.slice(end, end + ANCHOR_CONTEXT), a.suffix);
    const dist = Math.abs(i - a.start);
    if (score > bestScore || (score === bestScore && dist < bestDist)) {
      best = i;
      bestScore = score;
      bestDist = dist;
    }
  }
  return { from: best, to: best + a.quote.length };
}
